/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useServiceContext } from '../../root'
import FsiPicker, { FsiPickerOptions } from '../FsiPicker/FsiPicker'
import type { TreeNode } from '../FsiPickerNode'

interface FsiPickerModalProps {
  options: Partial<FsiPickerOptions>
  title: string
  isOpen: boolean
  onClose: () => void
  onSelect: (fsi: any) => void
  isNodeSelectable?: (node: TreeNode) => boolean
  showAddNewFolderModal?: boolean
}

export const FsiPickerModal: React.FC<FsiPickerModalProps> = ({
  options,
  title,
  isOpen,
  onClose,
  onSelect,
  isNodeSelectable,
  showAddNewFolderModal
}) => {
  const {
    service: {
      filter: { translate }
    }
  } = useServiceContext()
  const modalRef = useRef<HTMLDivElement>(null)
  const [selectedFsi, setSelectedFsi] = useState<any>(null)

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open')
    }
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [isOpen])

  const handleClose = () => {
    setSelectedFsi(null)
    onClose()
  }

  const handleOk = () => {
    if (selectedFsi) {
      onSelect(selectedFsi)
    } else {
      handleClose()
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="theme-sandman">
      {/* Modal Backdrop */}
      <div className="modal-backdrop fade in" onClick={handleBackdropClick} aria-hidden="true" />

      {/* Modal Dialog */}
      <div
        ref={modalRef}
        className="modal fade in fsi-picker-modal"
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
        onClick={handleBackdropClick}
        onKeyDown={handleKeyDown}
        id="fsi-picker-modal"
      >
        <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" className="close" onClick={handleClose} aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
              <h4 className="modal-title" id="ModalHeader">
                {title}
              </h4>
            </div>

            <div className="modal-body">
              <FsiPicker
                options={options}
                onSelect={(selectedIds, baseNodes) => {
                  // Handle both array of IDs (checkbox mode) and single node (radio mode)
                  if (Array.isArray(selectedIds)) {
                    // Array mode: Find the selected node in the tree
                    if (selectedIds.length > 0 && baseNodes && baseNodes.length > 0) {
                      const selectedId = selectedIds[0]

                      // Recursively find the node by ID
                      const findNodeById = (nodes: TreeNode[], targetId: string | number): TreeNode | null => {
                        for (const node of nodes) {
                          if (node.id === targetId) {
                            return node
                          }
                          if (node.children) {
                            const found = findNodeById(node.children, targetId)
                            if (found) return found
                          }
                        }
                        return null
                      }

                      const selectedNode = findNodeById(baseNodes, selectedId)
                      if (selectedNode) {
                        setSelectedFsi({
                          id: selectedNode.id,
                          name: selectedNode.name || ''
                        })
                      }
                    } else {
                      // No selection (unchecked)
                      setSelectedFsi(null)
                    }
                  } else {
                    // Single node mode: Use the node directly
                    const node = selectedIds as TreeNode
                    setSelectedFsi({
                      id: node.id,
                      name: node.name || ''
                    })
                  }
                }}
                isNodeSelectable={isNodeSelectable}
                showAddNewFolderModal={showAddNewFolderModal}
              />
            </div>

            <div className="modal-footer">
              <button
                data-action="cancel"
                className="btn btn-link"
                onClick={handleClose}
                id="15c9bcd4-44d0-4665-a770-6de1b34c40ef"
              >
                {translate('CANCEL')}
              </button>
              <button
                data-action="ok"
                className="btn btn-primary text-capitalize"
                onClick={handleOk}
                id="a8c826e0-e86b-48f4-8345-26c3badd8bf8"
              >
                {translate('OK')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FsiPickerModal
